import type { InterpolationFunction } from 'styled-components'
import styled from 'styled-components'

import { Dropdown, SideNavigation, TopNavigation } from '@loadsmart/loadsmart-ui'
import { getToken as token } from '@loadsmart/loadsmart-ui/dist/theming'
import { conditional, whenProps } from '@loadsmart/loadsmart-ui/dist/tools'
import { Divider, Layout } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

interface TransitionStyleOptions {
  property?: string
  timingFunction?: string
  duration?: string
}

function transitionStyle(options?: TransitionStyleOptions): string {
  return `
    transition-property: ${
      options?.property ||
      'background, border-color, box-shadow, color, fill, left, stroke, transform, opacity'
    };
    transition-timing-function: ${options?.timingFunction || 'cubic-bezier(0.4, 0, 0.2, 1)'};
    transition-duration: ${options?.duration || '450ms'};
    transition-delay: 0ms;
  `
}

export const StyledPageWIthSideNav = styled.div`
  display: flex;
  overflow: hidden;

  aside {
    a {
      text-decoration: none;
    }
  }
`

interface StyledMainWrapperProps {
  color?: string
  padding?: string | InterpolationFunction<any>
}

export const StyledMainWrapper = styled.div<StyledMainWrapperProps>`
  width: 100%;

  main {
    background-color: ${({ color }) => color || toCSSValue('color-background-primary')};
    height: calc(100vh - 60px);
    overflow-y: auto;
    width: 100%;
    max-width: none;
  }
`

export const StyledDropdownWrapper = styled(Layout.Box)`
  align-items: center;
  display: flex;

  div[role='menu'] {
    background-color: ${toCSSValue('color-background-primary')};
    max-height: unset;
    max-width: 300px;
    min-width: 200px;
    padding: 12px;
  }

  label > button {
    position: relative;
    right: 2px;
  }

  label:focus,
  label:focus-within,
  label.is-focused {
    color: ${toCSSValue('color-primary-60')};
  }
`

export const StyledDropdownItem = styled(Dropdown.Item)`
  background-color: ${toCSSValue('color-background-primary')};
  margin: 0;

  span:first-child {
    margin-right: 4px;
  }
`

export const StyledDivider = styled(Divider)`
  margin: 12px 0;
`

export const StyledTopNavigationWrapper = styled.div`
  width: 100%;
  ${TopNavigation.OpenSideNavButton} {
    display: none;
  }

  ${TopNavigation.Logo} {
    display: none;
  }

  @media (max-width: ${token('side-navigation-collapsing-breakpoint')}) {
    ${TopNavigation.OpenSideNavButton} {
      display: flex;
    }

    ${TopNavigation.Logo} {
      display: flex;
    }
  }
`

export const StyledSideNavigationWrapper = styled.div<{ $open: boolean }>`
  left: 0;
  position: static;
  top: 0;

  transform: ${conditional({
    'translateX(-100%)': whenProps({
      $open: false,
    }),
    'translateX(0px)': whenProps({
      $open: true,
    }),
  })};

  ${transitionStyle({
    property: 'transform',
    duration: '200ms',
  })}

  z-index: ${token('z-index-overlay')};

  ${SideNavigation.CloseButton} {
    display: none;
    margin-left: ${token('space-l')};

    svg {
      fill: ${token('color-neutral-white')};
    }
  }

  ${SideNavigation.Menu} {
    margin-top: ${toCSSValue('spacing-4')};

    div {
      font-size: ${toCSSValue('font-size-2')};
    }
  }

  aside {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  @media (max-width: ${token('side-navigation-collapsing-breakpoint')}) {
    position: fixed;

    aside {
      flex: 0 0 ${token('side-navigation-width-large')};
      width: ${token('side-navigation-width-large')};
    }

    ${SideNavigation.Logo} {
      display: none;
    }

    ${SideNavigation.CloseButton} {
      display: block;
    }
  }
`
